/**
 * The two values a device row PROMOTES out of its custom fields — status and battery.
 *
 * 🛑 The custom fields hold a device's CURRENT connectivity state; the heartbeat table holds
 * its HISTORY (App Server: helpers/ControlHelpers/ConnectivityStateWriter). `GET /devices`
 * already answers `status` + `battery` derived from those fields, but
 * `GET /devices/:deviceID/fields` answers the FIELDS ALONE — so a surface that refreshes one
 * device's fields (after a live check, say) has to re-derive the pair itself, or the row goes
 * on rendering the status it was listed with while the fields beneath it say otherwise.
 *
 * Mirrors routesV5/devices/classes.js and helpers/deviceStatus.js exactly, strictness
 * included: the canonical stored value is '1' for online and ANYTHING else is offline (the
 * camera syncs' 'live'/'offline' dialect was fixed at ingest, not here), and a device with no
 * status field at all is `null` — never swept, which is not the same as offline.
 */

/** The connectivity field, by the titles the different integrations write it under. */
export const STATUS_FIELD_TITLES = ['Status', 'Online Flag', 'Lock Online'];

/** Charge, 0–100, as written by the Control/TTLock syncs and every connectivity check. */
export const BATTERY_FIELD_TITLE = 'Electric Quantity';

/** The canonical stored value for an online device. */
export const DEVICE_STATUS_ONLINE = '1';

/**
 * @param {Array<{fieldTitle: string, value: *}>} fields - a device's custom fields
 * @returns {{status: (boolean|null), battery: (number|null)}} the promoted pair
 */
export const deriveDeviceStateFromFields = (fields) => {
	const list = Array.isArray(fields) ? fields : [];
	// First match by title, like the list endpoint — no filtering on fieldDeletedDate, so a
	// device that disagrees with the grid can only ever be the grid's own read being older.
	const statusField = list.find((f) => STATUS_FIELD_TITLES.includes(f?.fieldTitle));
	const batteryField = list.find((f) => f?.fieldTitle === BATTERY_FIELD_TITLE);
	const battery = batteryField ? parseInt(batteryField.value, 10) : NaN;
	return {
		status: statusField ? statusField.value === DEVICE_STATUS_ONLINE : null,
		battery: Number.isNaN(battery) ? null : battery
	};
};

export default deriveDeviceStateFromFields;
